import dayjs from "dayjs"
import {
  Alert,
  Button,
  Calendar,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography,
} from "antd"
import { useRef, useState } from "react"
import { v4 as uuidv4 } from "uuid"
import { siteWorkflowApi } from "../../utils/siteWorkflowApi"
import { workflowRequest } from "../../utils/workflowTransport"
import { inspectionStates, isOverdue } from "../../types/siteWorkflows"
import type {
  InspectionPlan,
  InspectionPlanWrite,
  PlannedInspection,
  WorkflowPage,
} from "../../types/siteWorkflows"
import { useWorkflowResource } from "./useWorkflowResource"
import {
  AssigneePicker,
  NoWorkflowItems,
  WorkflowError,
  WorkflowHistory,
  WorkflowPager,
} from "./WorkflowCommon"
import { useWorkflowSession } from "./workflowSession"
export function PlanEditor({
  site,
  enterprise,
  value,
  onCancel,
  onSaved,
}: {
  site: string
  enterprise: string
  value?: InspectionPlan
  onCancel: () => void
  onSaved: () => void
}) {
  const [form] = Form.useForm()
  const id = useRef(value?.uuid ?? uuidv4())
  const { userUuid } = useWorkflowSession()
  const [norm, setNorm] = useState(value?.normType ?? 10)
  const [template, setTemplate] = useState<string | null>(value?.templateUuid ?? null)
  const [revision, setRevision] = useState<string | null>(value?.templateRevisionUuid ?? null)
  const [search, setSearch] = useState("")
  const [templatePage, setTemplatePage] = useState(1)
  const templates = useWorkflowResource(
    norm === 10 ? `plan-templates/${search}/${templatePage}` : "",
    (signal) =>
      workflowRequest<
        WorkflowPage<{
          uuid: string
          name: string
          currentRevisionUuid: string | null
          validated: boolean
          sourceVersion: number
        }>
      >(
        `regulatory/templates?scope=site&limit=50&offset=${(templatePage - 1) * 50}&q=${encodeURIComponent(search)}`,
        signal,
      ),
  )
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>()
  const save = async () => {
    const fields = await form.validateFields().catch(() => null)
    if (!fields) return
    if (norm === 10 && (!template || !revision)) {
      setError(new Error("Choisissez un modèle avec sa révision exacte."))
      return
    }
    setBusy(true)
    setError(undefined)
    try {
      const body: InspectionPlanWrite = {
        uuid: id.current,
        physicalSiteUuid: site,
        title: fields.title,
        normType: norm,
        methodType: norm === 1 ? fields.methodType : 0,
        anchorDate: fields.anchorDate,
        intervalMonths: fields.intervalMonths,
        assigneeUuid: fields.assigneeUuid,
        templateUuid: norm === 10 ? template : null,
        templateRevisionUuid: norm === 10 ? revision : null,
        reminderDays: fields.reminderDays,
        archived: !!fields.archived,
        expectedVersion: value?.version,
        reason: fields.reason,
      }
      await siteWorkflowApi.savePlan(site, body, value?.uuid)
      onSaved()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open
      title={value ? "Modifier le programme" : "Programmer une inspection"}
      onCancel={onCancel}
      onOk={save}
      confirmLoading={busy}
      cancelText="Fermer"
      okText="Enregistrer"
      maskClosable={false}
      closable={!busy}
      cancelButtonProps={{ disabled: busy }}
    >
      <WorkflowError error={error} />
      <Alert
        type="info"
        message="La périodicité est un choix de gestion. Les dates suivent le calendrier d’origine, même si une visite est décalée. Aucune réponse passée n’est copiée."
      />
      <Form
        form={form}
        layout="vertical"
        disabled={busy}
        initialValues={{
          title: value?.title ?? "Inspection périodique",
          anchorDate: value?.anchorDate ?? new Date().toISOString().slice(0, 10),
          intervalMonths: value?.intervalMonths ?? 12,
          methodType: value?.methodType || 2,
          assigneeUuid: value?.assigneeUuid ?? userUuid,
          reminderDays: value?.reminderDays ?? 14,
          archived: value?.archived ?? false,
          reason: value ? "" : "Programme créé depuis le dossier du site",
        }}
      >
        <Form.Item
          name="title"
          label="Intitulé"
          rules={[{ required: true, whitespace: true }, { max: 250 }]}
        >
          <Input maxLength={250} />
        </Form.Item>
        <Form.Item label="Type">
          <Select
            value={norm}
            disabled={busy || !!value}
            onChange={setNorm}
            options={[
              { value: 10, label: "Réglementaire EN 1176" },
              { value: 1, label: "HIC EN 1177" },
              { value: 2, label: "EN 12503" },
            ]}
          />
        </Form.Item>
        {norm === 1 && (
          <Form.Item name="methodType" label="Méthode">
            <Select
              options={[
                { value: 1, label: "Hauteur de chute critique" },
                { value: 2, label: "Conformité de l’atténuation d’impact" },
              ]}
            />
          </Form.Item>
        )}
        {norm === 10 && (
          <Form.Item label="Modèle et révision retenue">
            <Select
              style={{ width: "100%" }}
              value={template ?? undefined}
              showSearch
              filterOption={false}
              onSearch={(v) => {
                setSearch(v)
                setTemplatePage(1)
              }}
              loading={templates.loading}
              onChange={(v) => {
                setTemplate(v)
                setRevision(
                  templates.data?.items.find((t) => t.uuid === v)?.currentRevisionUuid ?? null,
                )
              }}
              options={(templates.data?.items ?? [])
                .filter((t) => !!t.currentRevisionUuid)
                .map((t) => ({
                  value: t.uuid,
                  label: `${t.name} · v${t.sourceVersion} · ${t.validated ? "Relu" : "Non revu"}`,
                }))}
            />
            {(templates.data?.total ?? 0) > 50 && (
              <Button onClick={() => setTemplatePage((n) => n + 1)}>Autres modèles</Button>
            )}
            {revision && (
              <Typography.Paragraph type="secondary" style={{ overflowWrap: "anywhere" }}>
                Révision épinglée : {revision}. Les futures révisions ne remplaceront pas ce choix
                automatiquement.
              </Typography.Paragraph>
            )}
            <WorkflowError error={templates.error} retry={templates.reload} />
          </Form.Item>
        )}
        <Form.Item name="anchorDate" label="Première échéance" rules={[{ required: true }]}>
          <Input type="date" min="2000-01-01" max="2100-12-31" disabled={!!value || busy} />
        </Form.Item>
        <Form.Item
          name="intervalMonths"
          label="Périodicité en mois (0 = visite unique)"
          rules={[{ required: true }]}
        >
          <InputNumber min={0} max={120} precision={0} disabled={!!value || busy} />
        </Form.Item>
        <Form.Item name="assigneeUuid" label="Responsable" rules={[{ required: true }]}>
          <AssigneePicker enterprise={enterprise} required />
        </Form.Item>
        <Form.Item
          name="reminderDays"
          label="Afficher dans « À venir » à partir de (jours avant)"
          rules={[{ required: true }]}
        >
          <InputNumber min={0} max={365} precision={0} />
        </Form.Item>
        {value && (
          <Form.Item name="archived" label="Programme archivé" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
        <Form.Item
          name="reason"
          label="Motif"
          rules={[{ required: true, whitespace: true }, { max: 2000 }]}
        >
          <Input.TextArea maxLength={2000} rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
function OccurrenceEditor({
  value,
  operation,
  onCancel,
  onSaved,
}: {
  value: PlannedInspection
  operation: string
  onCancel: () => void
  onSaved: (v: PlannedInspection) => void
}) {
  const [form] = Form.useForm(),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>()
  const save = async () => {
    const fields = await form.validateFields().catch(() => null)
    if (!fields) return
    setBusy(true)
    setError(undefined)
    try {
      onSaved(
        await siteWorkflowApi.occurrence(
          value,
          operation,
          fields.reason,
          operation === "reschedule" ? fields.dueDate : undefined,
        ),
      )
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Modal
      open
      title={
        operation === "start"
          ? "Créer l’inspection prévue"
          : operation === "cancel"
            ? "Annuler cette visite"
            : "Déplacer cette visite"
      }
      onCancel={onCancel}
      onOk={save}
      confirmLoading={busy}
      okText="Confirmer"
      cancelText="Fermer"
      closable={!busy}
      maskClosable={false}
      cancelButtonProps={{ disabled: busy }}
    >
      <WorkflowError error={error} />
      {operation === "start" && (
        <Alert
          type="info"
          message="Un brouillon sera créé avec les coordonnées actuelles du site et la révision du programme. Vous pourrez compléter les équipements et la date réelle dans l’inspection."
        />
      )}
      <Form
        form={form}
        layout="vertical"
        disabled={busy}
        initialValues={{
          dueDate: value.dueDate,
          reason: operation === "start" ? "Création de l’inspection planifiée" : "",
        }}
      >
        {operation === "reschedule" && (
          <Form.Item name="dueDate" label="Nouvelle échéance" rules={[{ required: true }]}>
            <Input type="date" min="2000-01-01" max="2100-12-31" />
          </Form.Item>
        )}
        <Form.Item
          name="reason"
          label="Motif"
          rules={[{ required: true, whitespace: true }, { max: 2000 }]}
        >
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
export function InspectionPlanningPanel({
  site,
  enterprise,
}: {
  site: string
  enterprise: string
}) {
  const [month, setMonth] = useState(dayjs().startOf("month"))
  const calendar = useWorkflowResource(`calendar/${site}/${month.format("YYYY-MM")}`, (signal) =>
    siteWorkflowApi.calendar(
      site,
      month.startOf("month").subtract(7, "day").format("YYYY-MM-DD"),
      month.endOf("month").add(7, "day").format("YYYY-MM-DD"),
      signal,
    ),
  )
  const [page, setPage] = useState(1),
    [planPage, setPlanPage] = useState(1),
    [filter, setFilter] = useState("")
  const [editing, setEditing] = useState<InspectionPlan | "new">(),
    [operation, setOperation] = useState<{ value: PlannedInspection; name: string }>()
  const [history, setHistory] = useState<{ kind: string; id: string }>()
  const visits = useWorkflowResource(`visits/${site}/${filter}/${page}`, (signal) =>
    siteWorkflowApi.occurrences(site, filter, page, signal),
  )
  const plans = useWorkflowResource(`plans/${site}/${planPage}`, (signal) =>
    siteWorkflowApi.plans(site, planPage, signal),
  )
  const { inspectionHref } = useWorkflowSession()
  const reload = () => {
    visits.reload()
    plans.reload()
    calendar.reload()
  }
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap>
        <Button type="primary" onClick={() => setEditing("new")}>
          Programmer une inspection
        </Button>
        <Button onClick={reload}>Actualiser</Button>
      </Space>
      <Tabs
        defaultActiveKey="visits"
        items={[
          {
            key: "calendar",
            label: "Calendrier",
            children: (
              <>
                <WorkflowError error={calendar.error} retry={calendar.reload} />
                <div style={{ overflowX: "auto" }}>
                  <div style={{ minWidth: 650 }}>
                    <Calendar
                      value={month}
                      onPanelChange={(date) => setMonth(date.startOf("month"))}
                      onSelect={(date, info) => {
                        if (info.source === "date") setMonth(date)
                      }}
                      dateCellRender={(date) => (
                        <Space direction="vertical" size={2}>
                          {(calendar.data ?? [])
                            .filter((v) => v.dueDate === date.format("YYYY-MM-DD"))
                            .map((v) => (
                              <Button
                                key={v.uuid}
                                type="link"
                                size="small"
                                style={{
                                  padding: 0,
                                  height: "auto",
                                  whiteSpace: "normal",
                                  textAlign: "left",
                                  color:
                                    v.status === "cancelled"
                                      ? "#777"
                                      : isOverdue(v.dueDate, v.inspectionState)
                                        ? "#cf1322"
                                        : undefined,
                                }}
                                onClick={(event) => {
                                  event.stopPropagation()
                                  if (v.inspectionUuid)
                                    window.location.assign(
                                      inspectionHref(v.inspectionUuid, v.normType),
                                    )
                                  else if (v.status === "planned" && !v.planArchived)
                                    setOperation({ value: v, name: "start" })
                                }}
                              >
                                {v.title}
                                {v.status === "cancelled" ? " · Annulée" : ""}
                              </Button>
                            ))}
                        </Space>
                      )}
                    />
                  </div>
                </div>
              </>
            ),
          },
          {
            key: "visits",
            label: "Visites prévues",
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <Select
                  aria-label="Filtrer les visites"
                  value={filter}
                  style={{ minWidth: 220 }}
                  onChange={(v) => {
                    setFilter(v)
                    setPage(1)
                  }}
                  options={[
                    { value: "", label: "Toutes les visites" },
                    { value: "upcoming", label: "À venir" },
                    { value: "overdue", label: "En retard" },
                    { value: "planned", label: "À réaliser" },
                    { value: "linked", label: "Inspection créée" },
                    { value: "cancelled", label: "Annulées" },
                  ]}
                />
                <WorkflowError error={visits.error} retry={visits.reload} />
                {!visits.loading && !visits.data?.items.length && <NoWorkflowItems />}
                {visits.data?.items.map((v) => (
                  <Card size="small" key={v.uuid} title={v.title}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Space wrap>
                        <strong>{v.dueDate}</strong>
                        <Tag>
                          {v.status === "linked"
                            ? inspectionStates[v.inspectionState]
                            : v.status === "cancelled"
                              ? "Annulée"
                              : "Prévue"}
                        </Tag>
                        {v.planArchived && <Tag>Programme archivé</Tag>}
                        {!v.planArchived &&
                          v.status !== "cancelled" &&
                          isOverdue(v.dueDate, v.inspectionState) && (
                            <Tag color="red">En retard</Tag>
                          )}
                      </Space>
                      <Space wrap>
                        {v.inspectionUuid ? (
                          <a href={inspectionHref(v.inspectionUuid, v.normType)}>
                            Ouvrir l’inspection
                          </a>
                        ) : (
                          v.status === "planned" &&
                          !v.planArchived && (
                            <>
                              <Button onClick={() => setOperation({ value: v, name: "start" })}>
                                Créer l’inspection
                              </Button>
                              <Button
                                onClick={() => setOperation({ value: v, name: "reschedule" })}
                              >
                                Déplacer
                              </Button>
                              <Button onClick={() => setOperation({ value: v, name: "cancel" })}>
                                Annuler
                              </Button>
                            </>
                          )
                        )}
                        <Button
                          type="link"
                          onClick={() => setHistory({ kind: "occurrence", id: v.uuid })}
                        >
                          Historique
                        </Button>
                      </Space>
                    </Space>
                  </Card>
                ))}
                <WorkflowPager page={page} total={visits.data?.total} onChange={setPage} />
              </Space>
            ),
          },
          {
            key: "plans",
            label: "Programmes",
            children: (
              <Space direction="vertical" style={{ width: "100%" }}>
                <WorkflowError error={plans.error} retry={plans.reload} />
                {plans.data?.items.map((p) => (
                  <Card size="small" key={p.uuid} title={p.title}>
                    <Space wrap>
                      <span>
                        {p.intervalMonths ? `Tous les ${p.intervalMonths} mois` : "Visite unique"} ·
                        depuis le {p.anchorDate}
                      </span>
                      {p.archived && <Tag>Archivé</Tag>}
                      <Button onClick={() => setEditing(p)}>Modifier</Button>
                      <Button onClick={() => setHistory({ kind: "plan", id: p.uuid })}>
                        Historique
                      </Button>
                    </Space>
                  </Card>
                ))}
                <WorkflowPager page={planPage} total={plans.data?.total} onChange={setPlanPage} />
              </Space>
            ),
          },
        ]}
      />
      {editing && (
        <PlanEditor
          site={site}
          enterprise={enterprise}
          value={editing === "new" ? undefined : editing}
          onCancel={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined)
            reload()
          }}
        />
      )}
      {operation && (
        <OccurrenceEditor
          value={operation.value}
          operation={operation.name}
          onCancel={() => setOperation(undefined)}
          onSaved={(v) => {
            setOperation(undefined)
            reload()
            if (v.inspectionUuid)
              window.location.assign(inspectionHref(v.inspectionUuid, v.normType))
          }}
        />
      )}
      {history && (
        <Modal
          open
          title="Historique du planning"
          footer={null}
          onCancel={() => setHistory(undefined)}
        >
          <WorkflowHistory site={site} kind={history.kind} id={history.id} />
        </Modal>
      )}
    </Space>
  )
}
